import { Stack } from '@mui/material'
import { ChatsCircle, PushPin } from '@phosphor-icons/react'
import type { ExternalParticipant } from '@einwert/valuation-api-client'
import { ThreadWithMetadata } from 'ClientPortal/src/api/Queries/threads'
import { tokens, User } from 'ComponentLibrary'
import LoadingIndicator from 'ComponentLibrary/src/atoms/LoadingIndicator/LoadingIndicator'
import { Typography } from 'ComponentLibrary/src/piazza/text'

import { BORDER_WIDTH } from '../constants'
import type { AppraiserUser, CustomerUser } from '@einwert/valuation-api-client'
import MessageListHeaderThreadParticipants from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/MessageList/MessageListHeaderThreadParticipants'
import MessageListHeaderOptionsMenu from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/MessageList/MessageListHeaderOptionsMenu'
import { useTranslation } from 'react-i18next'
import { useUserLinkCopy } from 'ClientPortal/src/i18n/useUserLinkCopy'

type MessageListTaskDrawerHeaderProps = {
    thread: ThreadWithMetadata
    isPinned: boolean
    currentUser?: AppraiserUser | CustomerUser
    orderObserverIds: string[] | undefined
    orderObservers: User[]
    replaceParticipantIds: (participantIds: number[]) => void
    isPatchingThread: boolean
    pinThread: (threadId: string) => void
    unpinThread: (threadId: string) => void
    setIsEditingParticipants: (isEditingParticipants: boolean) => void
    isDeleteThreadPending: boolean
    externalParticipantUsers?: ExternalParticipant[]
}

const MessageListTaskDrawerHeader = ({
    thread,
    isPinned,
    currentUser,
    replaceParticipantIds,
    isPatchingThread,
    pinThread,
    unpinThread,
    setIsEditingParticipants,
    isDeleteThreadPending,
    orderObserverIds,
    orderObservers,
    externalParticipantUsers = [],
}: MessageListTaskDrawerHeaderProps) => {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.threadListPage' })
    const isThreadPending = isPatchingThread || isDeleteThreadPending
    const userLinkCopy = useUserLinkCopy()
    return (
        <>
            <Stack
                direction="row"
                padding={`${tokens.SizingSpacing200} ${tokens.SizingSpacing300}`}
                alignItems="center"
                width="100%"
                borderBottom={`${BORDER_WIDTH} solid ${tokens.ColorBorderDefault}`}
            >
                <Stack
                    direction="row"
                    height={tokens.SizingSpacing500}
                    alignItems="center"
                    gap={tokens.SizingSpacing200}
                    width="100%"
                    justifyContent="space-between"
                >
                    <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100}>
                        <ChatsCircle size={tokens.SizingIconM} color={tokens.ColorIconBoldest} />
                        <Typography variant="headline3" color={tokens.ColorTextBoldest}>
                            {t('comments')}
                        </Typography>
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            {thread.messages.length}
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing075}>
                            {isThreadPending && <LoadingIndicator isPending={isThreadPending} height={1} />}
                            <MessageListHeaderThreadParticipants
                                orderObserverIds={orderObserverIds}
                                orderObservers={orderObservers}
                                externalParticipantUsers={externalParticipantUsers}
                                participantUsers={thread.participant_users}
                                participantIds={thread.participant_ids}
                                currentUser={currentUser}
                                replaceParticipantIds={replaceParticipantIds}
                                setIsEditingParticipants={setIsEditingParticipants}
                                userLinkCopy={userLinkCopy}
                            />
                            <MessageListHeaderOptionsMenu
                                thread={thread}
                                isPinned={isPinned}
                                currentUser={currentUser}
                                isPatchingThread={isPatchingThread}
                                pinThread={pinThread}
                                unpinThread={unpinThread}
                                onReplaceParticipantIds={replaceParticipantIds}
                                onEditParticipants={setIsEditingParticipants}
                            />
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
            {isPinned && (
                <Stack
                    sx={{ backgroundColor: tokens.ColorSurfaceAccentRoyalDefault }}
                    padding={`${tokens.SizingSpacing100} ${tokens.SizingSpacing300}`}
                >
                    <Stack gap={tokens.SizingSpacing100} direction="row" height={tokens.SizingSpacing300} alignItems="center">
                        <PushPin size={tokens.SizingIconS} color={tokens.ColorIconBoldest} weight="bold" />
                        <Typography variant="textM" color={tokens.ColorTextBold}>
                            {t('pinned')}
                        </Typography>
                    </Stack>
                </Stack>
            )}
        </>
    )
}

export default MessageListTaskDrawerHeader
